import { useMemo, useState } from "react";
import { ExternalLink, Plus } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { useWorkspace } from "@/hooks/useWorkspace";
import { useWorkspaceAddons, type WorkspaceAddon } from "@/hooks/useWorkspaceAddons";
import {
  WORKSPACE_ADDON_PRICE,
  tierEntitlements,
  tierWorkspaceAddOnMax,
} from "@/lib/tier";
import { openBillingPortal } from "@/lib/billing";
import { getStripeEnvironment } from "@/lib/stripe";
import { supabase } from "@/integrations/supabase/client";
import { AddWorkspaceSeatDialog } from "@/components/AddWorkspaceSeatDialog";
import { toast } from "sonner";

function formatDate(iso: string | null): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString();
}

function isStillActive(addon: WorkspaceAddon): boolean {
  if (!["active", "trialing", "past_due", "canceled"].includes(addon.status)) return false;
  if (!addon.current_period_end) return addon.status !== "canceled";
  return new Date(addon.current_period_end) > new Date();
}

/**
 * Self-serve management for workspace add-on seats. Lists each Stripe
 * subscription the user owns under the `workspace_addon_monthly` SKU and
 * lets them cancel or resume individual seats without opening the Stripe
 * Billing Portal. Hidden for tiers that can't buy add-ons unless the
 * user has historical add-on rows.
 */
export const WorkspaceAddonsCard = () => {
  const { workspaces, workspaceLimit } = useWorkspace();
  const { addons, count: activeCount, loading, refresh } = useWorkspaceAddons();
  const [busyId, setBusyId] = useState<string | null>(null);
  const [addOpen, setAddOpen] = useState(false);

  const primary = workspaces.find((w) => w.is_primary) ?? workspaces[0];
  const tier = primary?.account_tier ?? null;
  const addonMax = tierWorkspaceAddOnMax(tier);
  const ent = tierEntitlements(tier);

  const visibleAddons = useMemo(
    () => addons.filter(isStillActive).sort((a, b) => a.stripe_subscription_id.localeCompare(b.stripe_subscription_id)),
    [addons],
  );

  // Hide entirely if the plan doesn't support add-ons and the user has
  // never bought one. (If they have historical seats, keep the card visible
  // so they can still cancel them.)
  if ((addonMax === 0 || addonMax === undefined) && visibleAddons.length === 0) {
    return null;
  }

  const purchased = visibleAddons.length;
  const remainingPurchasable = addonMax === null ? null : Math.max(0, addonMax - purchased);

  const toggle = async (addon: WorkspaceAddon, action: "cancel" | "resume") => {
    setBusyId(addon.id);
    const { data, error } = await supabase.functions.invoke("manage-workspace-addon", {
      body: {
        addonId: addon.id,
        action,
        environment: getStripeEnvironment(),
      },
    });
    const errPayload = (data as { error?: string } | null)?.error;
    setBusyId(null);
    if (error || errPayload) {
      return toast.error(errPayload || error?.message || "Could not update seat");
    }
    toast.success(action === "cancel" ? "Seat scheduled to cancel at period end" : "Seat resumed");
    await refresh();
  };

  return (
    <>
      <Card className="shadow-soft">
        <CardHeader>
          <CardTitle>Manage Add-Ons</CardTitle>
          <CardDescription>
            Extra workspace seats at ${WORKSPACE_ADDON_PRICE.monthly}/month each.
            Cancelled seats keep working until the end of their billing period.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4 text-sm">
          <div className="flex flex-wrap items-center justify-between gap-2 text-muted-foreground">
            <span>
              Using <strong className="text-foreground">{workspaces.length}</strong>
              {workspaceLimit !== null && (
                <> of <strong className="text-foreground">{workspaceLimit}</strong></>
              )}{" "}
              workspaces · <strong className="text-foreground">{activeCount}</strong> active add-on seat
              {activeCount === 1 ? "" : "s"}
              {remainingPurchasable !== null && ent.workspaces !== null && (
                <> · {remainingPurchasable} more available</>
              )}
              {remainingPurchasable === null && ent.workspaces !== null && (
                <> · unlimited additional seats</>
              )}
            </span>
          </div>

          {visibleAddons.length === 0 ? (
            <p className="text-muted-foreground">
              You don't have any workspace add-on seats yet.
            </p>
          ) : (
            <ul className="divide-y divide-border rounded-md border border-border">
              {visibleAddons.map((addon, idx) => {
                const cancelled = addon.cancel_at_period_end || addon.status === "canceled";
                const periodEnd = formatDate(addon.current_period_end);
                return (
                  <li
                    key={addon.id}
                    className="flex flex-wrap items-center justify-between gap-3 px-3 py-3"
                  >
                    <div className="min-w-0 flex-1 space-y-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="font-medium">Workspace seat #{idx + 1}</span>
                        <span className="text-xs text-muted-foreground">
                          ${WORKSPACE_ADDON_PRICE.monthly}/mo
                        </span>
                        {addon.status === "past_due" && (
                          <Badge variant="destructive" className="text-[10px]">Past due</Badge>
                        )}
                        {cancelled && (
                          <Badge variant="secondary" className="text-[10px]">Cancelling</Badge>
                        )}
                      </div>
                      <p className="text-xs text-muted-foreground">
                        {addon.status === "past_due"
                          ? "Payment failed — update your card in the billing portal."
                          : cancelled
                            ? `Cancels on ${periodEnd}`
                            : `Renews on ${periodEnd}`}
                      </p>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {addon.status === "past_due" ? (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => void openBillingPortal()}
                        >
                          <ExternalLink className="h-4 w-4" />
                          Update card
                        </Button>
                      ) : cancelled ? (
                        <Button
                          size="sm"
                          variant="outline"
                          disabled={busyId === addon.id}
                          onClick={() => void toggle(addon, "resume")}
                        >
                          {busyId === addon.id ? "Resuming…" : "Resume seat"}
                        </Button>
                      ) : (
                        <Button
                          size="sm"
                          variant="outline"
                          disabled={busyId === addon.id}
                          onClick={() => void toggle(addon, "cancel")}
                        >
                          {busyId === addon.id ? "Cancelling…" : "Cancel seat"}
                        </Button>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          )}

          {(addonMax === null || (addonMax !== undefined && addonMax > 0)) && (
            <div className="flex flex-wrap gap-2">
              <Button
                variant="outline"
                disabled={
                  loading ||
                  (remainingPurchasable !== null && remainingPurchasable === 0)
                }
                onClick={() => setAddOpen(true)}
              >
                <Plus className="h-4 w-4" />
                Add another seat
              </Button>
              <Button variant="ghost" onClick={() => void openBillingPortal()}>
                <ExternalLink className="h-4 w-4" />
                Open billing portal
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      <AddWorkspaceSeatDialog open={addOpen} onOpenChange={setAddOpen} />
    </>
  );
};
